import type { Workitem } from '@rhythmic/db/types'
import type { KanbanContext, UpdateKanbanBoardItemPayload } from '@rhythmic/types'
import { KanbanContextSchema, UpdateKanbanBoardItemSchema } from '@rhythmic/types'
import type { Collection, Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getBoardItemsCollection, getWorkitemsCollection } from '../getCollections'

const logger = useRhythmicLogger('useKanbanBoardContext.updateBoardItem')

function validateParams(payload: UpdateKanbanBoardItemPayload, context: KanbanContext | undefined) {
  if (!context) throw new Error('Kanban board context is not ready: missing context for updating a board item')

  logger.debug('Validating parameters for handleUpdateBoardItem', {
    payload,
    context,
  })
  const parsedPayload = UpdateKanbanBoardItemSchema.parse(payload)
  const parsedContext = KanbanContextSchema.parse(context)

  return {
    context: parsedContext,
    payload: parsedPayload,
  }
}

export default function handleUpdateBoardItem(
  payload: UpdateKanbanBoardItemPayload,
  context: KanbanContext | undefined,
  existingCollections?: {
    boardItems?: Collection<HarmonyPackageBoardWorkitem>
    workItems?: Collection<Workitem>
  },
): Transaction {
  logger.debug('handleUpdateBoardItem called', { payload, context })
  const { context: validatedContext, payload: validatedPayload } = validateParams(payload, context)
  const boardItemsCollection = existingCollections?.boardItems ?? getBoardItemsCollection(validatedContext.boardId)
  const workItemsCollection = existingCollections?.workItems ?? getWorkitemsCollection(validatedContext.teamId)

  const mutations = {
    validatedPayload,
    validatedContext,
  }

  const updateBoardItemAction = createOptimisticAction<typeof mutations>({
    onMutate: (mutationPayload: typeof mutations) => {
      logger.debug('Applying optimistic update for board item', mutationPayload)
      // Update the board item entry
      boardItemsCollection.update(mutationPayload.validatedPayload.boardItemId, (draft) => {
        draft.column_id = mutationPayload.validatedPayload.column_id ?? draft.column_id
        draft.position = mutationPayload.validatedPayload.position ?? draft.position
      })

      // If there are workitem updates, apply them
      if (
        mutationPayload.validatedPayload.workitem &&
        (mutationPayload.validatedPayload.workitem.status_code ||
          mutationPayload.validatedPayload.workitem.priority ||
          mutationPayload.validatedPayload.workitem.parent_workitem)
      ) {
        if (!mutationPayload.validatedPayload.workitem_id) {
          logger.error('Unable to determine associated workitem for board item update', {
            boardId: validatedContext.boardId,
            boardItemId: mutationPayload.validatedPayload.boardItemId,
          })
          throw new Error('Failed to locate associated workitem for board item update')
        }
        logger.debug('Applying optimistic workitem updates for board item', {
          boardId: validatedContext.boardId,
          boardItemId: mutationPayload.validatedPayload.boardItemId,
          workitemId: mutationPayload.validatedPayload.workitem_id,
        })
        workItemsCollection.update(mutationPayload.validatedPayload.workitem_id, (draft: Workitem) => {
          draft.status = mutationPayload.validatedPayload.workitem?.workitem_status ?? draft.status
          draft.parent_workitem = mutationPayload.validatedPayload.workitem?.parent_workitem ?? draft.parent_workitem
          draft.priority = mutationPayload.validatedPayload.workitem?.priority ?? draft.priority
        })
      }
    },
    mutationFn: async (mutationPayload: typeof mutations) => {
      const waitables = []
      if (mutationPayload.validatedPayload.column_id || mutationPayload.validatedPayload.position) {
        const bwiTx = (await $fetch(`/api/board-workitems/${mutationPayload.validatedPayload.boardItemId}`, {
          method: 'PUT',
          body: {
            id: mutationPayload.validatedPayload.boardItemId,
            ...(mutationPayload.validatedPayload.column_id !== undefined && {
              column_id: mutationPayload.validatedPayload.column_id,
            }),
            ...(mutationPayload.validatedPayload.position !== undefined && {
              position: mutationPayload.validatedPayload.position,
            }),
          },
        })) as any
        if (boardItemsCollection.utils.awaitTxId) {
          waitables.push(boardItemsCollection.utils.awaitTxId(Number.parseInt(bwiTx.txid), 300000))
        }
      }
      if (mutationPayload.validatedPayload.workitem) {
        const result = (await $fetch(`/api/workitems/${mutationPayload.validatedPayload.workitem_id}`, {
          method: 'PUT',
          body: {
            id: mutationPayload.validatedPayload.workitem_id,
            ...(mutationPayload.validatedPayload.workitem.parent_workitem !== undefined && {
              parent_workitem: mutationPayload.validatedPayload.workitem.parent_workitem,
            }),
            ...(mutationPayload.validatedPayload.workitem.priority !== undefined && {
              priority: mutationPayload.validatedPayload.workitem.priority,
            }),
            ...(mutationPayload.validatedPayload.workitem.workitem_status !== undefined && {
              status: mutationPayload.validatedPayload.workitem.workitem_status,
            }),
          },
        })) as any
        if (mutationPayload.validatedPayload.workitem && workItemsCollection.utils?.awaitTxId) {
          waitables.push(workItemsCollection.utils.awaitTxId(Number.parseInt(result.txid), 300000))
        }
      }

      if (waitables.length === 0) return { txid: undefined }

      await Promise.all(waitables)
    },
  })

  const updateTx = updateBoardItemAction(mutations)

  logger.info('Kanban board item updated successfully', {
    boardId: validatedContext.boardId,
    boardItemId: validatedPayload.boardItemId,
    updateTx: {
      status: updateTx.isPersisted,
      state: updateTx.state,
    },
  })

  return updateTx
}
